import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { trackEvent, trackPageView } from "@/lib/track";

import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Services from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import Contact from "@/pages/Contact";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import CataractLanding from "@/pages/CataractLanding";
import IolGuide from "@/pages/IolGuide";
import OculoplastyLanding from "@/pages/OculoplastyLanding";
import OculoplastyGuide from "@/pages/OculoplastyGuide";
import DigitalCard from "@/pages/DigitalCard";
import HomeVisit from "@/pages/HomeVisit";
import HomeVisitFlyer from "@/pages/HomeVisitFlyer";
import NotFound from "@/pages/NotFound";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";

function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
}

function App() {
  // Global click delegate — fires conversion events for any Call / WhatsApp /
  // Directions / Book Appointment link on the site without touching each file.
  // gtag.js itself is loaded via the standard snippet in /public/index.html.
  useEffect(() => {
    const handler = (e) => {
      const a = e.target.closest && e.target.closest("a[href]");
      if (!a) return;
      const href = a.getAttribute("href") || "";
      const location = (a.getAttribute("data-track-location") || a.textContent || "").trim().slice(0, 60);

      if (href.startsWith("tel:")) {
        trackEvent("call_click", { event_category: "engagement", event_label: location, phone: href.replace("tel:", "") });
      } else if (href.includes("wa.me") || href.includes("api.whatsapp.com")) {
        trackEvent("whatsapp_click", { event_category: "engagement", event_label: location });
      } else if (href.includes("google.com/maps") || href.includes("maps.google.com") || href.includes("goo.gl/maps")) {
        trackEvent("directions_click", { event_category: "engagement", event_label: location });
      } else if (href.startsWith("mailto:")) {
        trackEvent("email_click", { event_category: "engagement", event_label: location });
      } else if (/\/contact\b/.test(href) || /book[- ]appointment/i.test(location)) {
        trackEvent("book_appointment_click", { event_category: "engagement", event_label: location });
      }
    };
    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, []);

  return (
    <div className="App grain">
      <BrowserRouter>
        <RouteTracker />
        <Routes>
          <Route element={<Layout />}>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/:slug" element={<ServiceDetail />} />
            <Route path="/cataract" element={<CataractLanding />} />
            <Route path="/cataract/iol-guide" element={<IolGuide />} />
            <Route path="/eyelid-oculoplasty" element={<OculoplastyLanding />} />
            <Route path="/eyelid-oculoplasty/guide" element={<OculoplastyGuide />} />
            <Route path="/eye-checkup-at-home" element={<HomeVisit />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            {/* Legacy redirects — preserve SEO after botox terminology removal */}
            <Route path="/services/botox-around-eyes" element={<Navigate to="/services/periocular-rejuvenation" replace />} />
            <Route path="/blog/how-long-does-botox-last" element={<Navigate to="/blog/how-long-does-botulinum-toxin-last" replace />} />
            <Route path="*" element={<NotFound />} />
          </Route>
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/dr-gurkiran-kaur" element={<DigitalCard />} />
          <Route path="/eye-checkup-at-home/flyer" element={<HomeVisitFlyer />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
